import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, GraduationCap, Trophy, Clock,
  TrendingUp, CheckCircle, XCircle, Search, Download,
  Award, BarChart3, UserCheck, AlertTriangle, RefreshCw, RotateCcw
} from 'lucide-react';

const API = API_BASE;

export default function TrainingAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trainees, setTrainees] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [traineesRes, statsRes] = await Promise.all([
        axios.get(`${API}/training/admin/trainees`),
        axios.get(`${API}/training/admin/stats`)
      ]);
      setTrainees(traineesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Datos actualizados.');
  };

  const handlePromoteTrainee = async (traineeId) => {
    if (!confirm('¿Estás seguro de promover este trainee a usuario de crédito?')) return;
    
    try {
      await axios.post(`${API}/training/admin/promote/${traineeId}`);
      toast.success('Trainee promovido exitosamente.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al promover trainee.');
    }
  };

  const handleResetProgress = async (trainee) => {
    const name = trainee.full_name || trainee.email;
    const confirmMsg =
      `¿Resetear TODO el progreso de ${name}?\n\n` +
      `Se borrará permanentemente:\n` +
      `  • Todas las lecciones completadas\n` +
      `  • Todos los intentos de quiz (${trainee.quizzes_attempted || 0})\n` +
      `  • Intentos del examen final\n` +
      `  • Certificación (si tiene)\n` +
      `  • Historial de actividad/tiempo\n\n` +
      `El trainee empezará desde cero. Esta acción NO se puede deshacer.`;
    if (!confirm(confirmMsg)) return;
    
    try {
      const res = await axios.post(`${API}/training/admin/reset-trainee-progress/${trainee.user_id}`);
      toast.success(`Progreso reseteado para ${name}.`, {
        description: `Examen final: ${res.data.final_exams_deleted}, Certificaciones: ${res.data.certifications_deleted}`
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear progreso.');
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await axios.get(`${API}/training/admin/export-report`);
      const { filename, content } = response.data;
      
      // Download CSV
      const blob = new Blob([content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado.');
    } catch (error) {
      toast.error('Error al exportar reporte.');
    }
  };

  const filteredTrainees = trainees.filter(t => {
    const matchesSearch = t.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const progress = t.progress_percentage || 0;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'completed' && progress >= 100) ||
                         (filterStatus === 'in_progress' && progress > 0 && progress < 100) ||
                         (filterStatus === 'not_started' && progress === 0) ||
                         (filterStatus === 'certified' && t.status === 'certified');
    return matchesSearch && matchesStatus;
  });

  const formatHours = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusBadge = (trainee) => {
    if (trainee.status === 'certified') {
      return <Badge className="bg-green-500"><Award className="h-3 w-3 mr-1" /> Certificado</Badge>;
    }
    const progress = trainee.progress_percentage || 0;
    if (progress >= 100) {
      return <Badge className="bg-blue-500">Completado</Badge>;
    }
    if (progress > 0) {
      return <Badge variant="secondary">En Progreso</Badge>;
    }
    return <Badge variant="outline">Sin Iniciar</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/training')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Dashboard de Administración
            </h1>
            <p className="text-muted-foreground">Monitorea el progreso de los trainees.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={handleExportReport}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Reporte
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Trainees</p>
                <p className="text-3xl font-bold">{stats?.total_trainees || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Progreso</p>
                <p className="text-3xl font-bold">{stats?.in_progress || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Certificados</p>
                <p className="text-3xl font-bold">{stats?.certified || 0}</p>
              </div>
              <Trophy className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Promedio de Progreso</p>
                <p className="text-3xl font-bold">{Math.round(stats?.avg_progress || 0)}%</p>
              </div>
              <GraduationCap className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trainees Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Trainees</CardTitle>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="not_started">Sin Iniciar</SelectItem>
                  <SelectItem value="in_progress">En Progreso</SelectItem>
                  <SelectItem value="completed">Completado</SelectItem>
                  <SelectItem value="certified">Certificados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trainee</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Progreso</TableHead>
                <TableHead>Lecciones Vistas</TableHead>
                <TableHead>Quizzes Aprobados</TableHead>
                <TableHead>Tiempo Invertido</TableHead>
                <TableHead>Idioma</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrainees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No se encontraron trainees.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrainees.map((trainee) => {
                  const lessonsPct = trainee.lessons_viewed_percentage || 0;
                  const progressPct = trainee.progress_percentage || 0;
                  // Engagement gap: a trainee passing quizzes without watching lessons
                  // shows a big delta between progress and lessons viewed.
                  const gap = progressPct - lessonsPct;
                  const isSuspicious = gap >= 30 && progressPct >= 30;
                  return (
                  <TableRow key={trainee.user_id || trainee.id} data-testid={`trainee-row-${trainee.user_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {trainee.full_name}
                          {isSuspicious && (
                            <span
                              title="Aprobó quizzes saltándose la mayoría de las lecciones"
                              className="inline-flex items-center text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"
                              data-testid={`trainee-flag-${trainee.user_id}`}
                            >
                              ⚠︎ Engagement bajo
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">{trainee.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(trainee)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={progressPct} className="w-24 h-2" />
                        <span className="text-sm">{Math.round(progressPct)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={lessonsPct}
                          className={`w-24 h-2 ${isSuspicious ? '[&>div]:bg-amber-500' : ''}`}
                        />
                        <span className={`text-sm font-medium ${isSuspicious ? 'text-amber-700' : ''}`}>
                          {Math.round(lessonsPct)}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({trainee.lessons_completed || 0}/{trainee.lessons_total || 0})
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {trainee.quizzes_passed || 0}
                        <span className="text-muted-foreground"> / {trainee.quizzes_attempted || 0}</span>
                      </span>
                    </TableCell>
                    <TableCell>{formatHours(trainee.hours_completed || 0)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {trainee.training_language === 'es' ? 'Español' : 'English'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {trainee.status === 'certified' && !trainee.is_promoted ? (
                          <Button 
                            size="sm" 
                            onClick={() => handlePromoteTrainee(trainee.user_id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Promover
                          </Button>
                        ) : trainee.is_promoted ? (
                          <Badge className="bg-green-100 text-green-800">Promovido</Badge>
                        ) : null}
                        {/* Reset button - shown if the trainee has any progress/activity to wipe */}
                        {(progressPct > 0 || (trainee.quizzes_attempted || 0) > 0 || (trainee.hours_completed || 0) > 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            onClick={() => handleResetProgress(trainee)}
                            title="Resetear todo el progreso de este trainee"
                            data-testid={`reset-trainee-btn-${trainee.user_id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Resetear
                          </Button>
                        )}
                        {progressPct === 0 && (trainee.quizzes_attempted || 0) === 0 && (trainee.hours_completed || 0) === 0 && !trainee.is_promoted && (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Module Completion Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tasa de Completación por Módulo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats?.module_completion?.map((module, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Módulo {module.number}: {module.title}</span>
                  <span className="font-medium">{module.completion_rate}%</span>
                </div>
                <Progress value={module.completion_rate} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Time Statistics - Removed Quiz Performance section (evaluations are now in-person) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estadísticas de Tiempo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <span>Tiempo Promedio de Capacitación</span>
                </div>
                <span className="text-lg font-bold text-blue-600">{formatHours(stats?.avg_time_to_certify / 60)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
